import { useRef } from 'react';
import { createGetInputId } from 'modules/frequencyManagement/commonUtils';
import { FrequencyMode } from 'modules/frequencyManagement/lib/types';
import { FocusContext } from './FocusContext';

export const FocusProvider = ({ children }: { children: React.ReactNode }) => {
  /**
   * This logic for managing focus is a workaround for the issue that a simple
   * autoFocus attribute on the input elements clashes with the search input on
   * the Message Types card. (When a search query is entered then removed, the
   * inputs are rerendered and they hijack the focus from the search input.)
   *
   * The approach here is to store the id of the input that should be focused,
   * and have that id imperatively updated when the user triggers focus by 1)
   * clicking the edit button to initiate edits, or 2) changing frequency mode
   * via the select.
   */

  // Using ref to detach from the render lifecycle
  const focusId = useRef<string | null>(null);

  const setFocusId = (id: string | null) => {
    focusId.current = id;
  };

  const setFocusIdForRootName = (mode: FrequencyMode, rootName: string | undefined) => {
    const getInputId = createGetInputId(mode);
    const inputId = getInputId(rootName);
    setFocusId(inputId);
  };

  const focus = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.focus();
    setFocusId(null);
  };

  return (
    <FocusContext.Provider
      value={{
        focusId,
        setFocusIdForRootName,
        focus,
      }}
    >
      {children}
    </FocusContext.Provider>
  );
};
